// Phase I5.1 — Display preferences (Daily Acca + Super Acca direction
// toggles).
//
// Anon-readable, no AppShell. Uses localStorage only — no DB writes,
// no auth required. The Daily Acca / Super Acca pages already read
// these keys on mount; flipping a toggle here takes effect on next
// visit.
//
// Doctrine: never fabricate. We expose three real directions per
// surface (A / B / C); the canonical default is A.

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  DAILY_ACCA_DIRECTION_KEY,
  SUPER_ACCA_DIRECTION_KEY,
  type Direction,
  getDirectionPreference,
  setDirectionPreference,
} from "../../settings/getDirectionPreference.ts";

const OPTIONS: ReadonlyArray<{ id: Direction; label: string; sub: string }> = [
  { id: "A", label: "A", sub: "Canonical" },
  { id: "B", label: "B", sub: "Split rail" },
  { id: "C", label: "C", sub: "Terminal" },
];

export function DisplayPreferencesPage(): JSX.Element {
  const [daily, setDaily] = useState<Direction>(() =>
    getDirectionPreference(DAILY_ACCA_DIRECTION_KEY),
  );
  const [sup, setSup] = useState<Direction>(() =>
    getDirectionPreference(SUPER_ACCA_DIRECTION_KEY),
  );

  const handleDaily = (d: Direction) => {
    setDaily(d);
    setDirectionPreference(DAILY_ACCA_DIRECTION_KEY, d);
  };
  const handleSup = (d: Direction) => {
    setSup(d);
    setDirectionPreference(SUPER_ACCA_DIRECTION_KEY, d);
  };

  return (
    <div
      className="sa-root"
      style={{
        background: "var(--sa-bg-base)",
        minHeight: "var(--vh-full)",
        color: "var(--sa-fg-primary)",
        fontFamily: "var(--sa-font-body)",
      }}
    >
      <header
        style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--sa-hairline)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          maxWidth: 1180,
          margin: "0 auto",
        }}
      >
        <Link
          to="/"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--sa-fg-secondary)",
            textDecoration: "none",
          }}
        >
          ← STRIKZA
        </Link>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            letterSpacing: "0.18em",
            color: "var(--sa-fg-muted)",
            textTransform: "uppercase",
          }}
        >
          Settings · Display
        </div>
      </header>

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "40px 24px 80px" }}>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.22em",
            color: "var(--sa-accent-saffron)",
            textTransform: "uppercase",
          }}
        >
          Display preferences
        </div>
        <h1
          style={{
            fontFamily: "var(--sa-font-display)",
            fontSize: 32,
            fontWeight: 600,
            marginTop: 12,
          }}
        >
          Choose how Daily Acca and Super Acca render.
        </h1>
        <p
          style={{
            marginTop: 10,
            fontSize: 14,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          Changes apply on next visit. Saved on this device only — no account
          required.
        </p>

        <Section
          title="Daily Acca direction"
          value={daily}
          onChange={handleDaily}
        />
        <Section
          title="Super Acca direction"
          value={sup}
          onChange={handleSup}
        />
      </main>
    </div>
  );
}

function Section({
  title,
  value,
  onChange,
}: {
  title: string;
  value: Direction;
  onChange: (d: Direction) => void;
}) {
  return (
    <section style={{ marginTop: 32 }}>
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          letterSpacing: "0.18em",
          color: "var(--sa-fg-muted)",
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      <div
        role="radiogroup"
        aria-label={title}
        style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        {OPTIONS.map((opt) => {
          const active = opt.id === value;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.id)}
              style={{
                flex: "1 1 140px",
                padding: "14px 18px",
                borderRadius: 6,
                background: active ? "var(--sa-amber-glow)" : "transparent",
                border: `1px solid ${active ? "var(--sa-accent-saffron)" : "var(--sa-hairline)"}`,
                color: active ? "var(--sa-accent-saffron)" : "var(--sa-fg-primary)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--sa-font-stat)",
                  fontSize: 28,
                  lineHeight: 1,
                }}
              >
                {opt.label}
              </div>
              <div
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 10.5,
                  marginTop: 6,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: active ? "var(--sa-accent-saffron)" : "var(--sa-fg-muted)",
                }}
              >
                {opt.sub}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
